import { useCallback, useState, useEffect } from 'react';
import { useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { getPrefName } from '@/data/prefectures';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';

type LogicProps = {
  listing: HospitalListingOutput;
  mapMarkerPortalOfficeId?: number | null;
  onMarkerClick?: (portalOfficeId: number) => void;
};

export const useLogic = ({
  listing,
  mapMarkerPortalOfficeId,
  onMarkerClick,
}: LogicProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [isShowInfoWindow, setIsShowInfoWindow] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const currentId = listing.portal_office_id as number;

  // デバイスタイプの検出
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
          navigator.maxTouchPoints > 0 ||
          (navigator as any).msMaxTouchPoints > 0,
      );
    };

    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);

    return () => {
      window.removeEventListener('resize', checkTouchDevice);
    };
  }, []);

  // 外部からのホバー状態を監視
  useEffect(() => {
    if (mapMarkerPortalOfficeId === currentId) {
      setIsShowInfoWindow(true);
      setIsHovered(true); // ステーション一覧ホバー時にマーカーを拡大表示
    } else if (
      mapMarkerPortalOfficeId !== null &&
      mapMarkerPortalOfficeId !== currentId
    ) {
      // 他のオフィスがホバーされた場合は非表示
      setIsShowInfoWindow(false);
      setIsHovered(false); // 他のステーションがホバーされた時は通常サイズに戻す
    } else if (mapMarkerPortalOfficeId === null) {
      // nullの場合は非表示にする（クリックによるOFF操作）
      setIsShowInfoWindow(false);
      setIsHovered(false); // ホバー解除時は通常サイズに戻す
    }
  }, [mapMarkerPortalOfficeId, currentId]);

  const handleMarkerClick = useCallback(() => {
    if (onMarkerClick) {
      // 新しい統合されたクリック処理を使用（InfoWindow制御 + スクロール）
      onMarkerClick(currentId);
    } else {
      // fallback: 外部の状態管理がない場合は従来の動作
      setIsShowInfoWindow((prev) => !prev);
    }
  }, [onMarkerClick, currentId]);

  // ホバーイベントハンドラー（マウス操作時のみ）
  const handleMarkerMouseEnter = useCallback(() => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  }, [isTouchDevice]);

  const handleMarkerMouseLeave = useCallback(() => {
    if (!isTouchDevice) {
      setIsHovered(false);
    }
  }, [isTouchDevice]);

  const officeName = listing.portal_office?.name;
  const officeAddress = [
    getPrefName(listing.portal_office?.pref || 0),
    listing.portal_office?.city,
    listing.portal_office?.area,
    listing.portal_office?.building || '',
  ].join('');
  const markerPosition = {
    lat: listing.latitude as number,
    lng: listing.longitude as number,
  };
  const isIbowOffice = !!listing.portal_office?.ibow_office_id;

  return {
    markerRef,
    marker,
    isShowInfoWindow,
    handleMarkerClick,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    isHovered,
    isTouchDevice,
    officeName,
    officeAddress,
    markerPosition,
    isIbowOffice,
  };
};
